import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import md5 from 'blueimp-md5';
import Gravatar from 'react-gravatar';

import { BasicModal, DefaultButton, PaginationTable, RowLink } from 'common/components';
import { getAllTags } from 'backoffice/users/api';
import UserAddTagForm from 'backoffice/users/_components/UserAddTagForm';

import { withTheme } from 'common/styling/theme';

import ClientsCellWithDropDown from './ClientsCellWithDropDown';
import PartnerTagsCell from './PartnerTagsCell';
import { staticStyles, getDynamicStyles } from './style';

import { addPartnerAccountTags, deletePartnerAccountTag } from '../../api';

const PARTNER_FIXED_TAG_TYPE = 'Trading & Commission';

const formatAmountWithThousands = amount => {
  if (amount === null || amount === undefined || amount === '') return amount;
  const parts = String(amount).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const PartnersTable = ({ data, onRowClick, getList, loading, history, location, params, theme }) => {
  const [allTags, setAllTags] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);
  const [tagToDelete, setTagToDelete] = useState(null);

  useEffect(() => {
    if (!editingAccount || allTags.length) return undefined;

    let requestActive = true;
    getAllTags()
      .then(({ data: tags }) => {
        if (requestActive) setAllTags(tags || []);
      })
      .catch(() => undefined);

    return () => {
      requestActive = false;
    };
  }, [editingAccount, allTags.length]);

  const reloadPartners = () =>
    getList({
      search: {
        ...(params.search || {}),
        page_size: (params.search && params.search.page_size) || 10,
      },
    });

  const submitTags = tagIds =>
    addPartnerAccountTags(editingAccount.id, tagIds).then(() => {
      setEditingAccount(null);
      reloadPartners();
    });

  const confirmDelete = () => {
    const { accountId, tag } = tagToDelete;
    deletePartnerAccountTag(accountId, tag.id).then(() => {
      setTagToDelete(null);
      reloadPartners();
    });
  };

  /* eslint-disable */
  const columns = [
    {
      Header: () => null,
      id: 'avatar',
      Cell: ({
        row: {
          original: { email, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`} isPreviewCell>
          <Gravatar md5={md5(email)} size={35} />
        </RowLink>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'justFullNameSlashIb',
      accessor: 'full_name',
      Cell: ({
        row: {
          original: { fullName, ibAccounts, id },
        },
      }) => {
        return (
          <RowLink link={`/backoffice/users/${id}`} isPreviewCell>
            <div className="FullNameAndIbCell">
              <span className="FullNameAndIbCell__FullName">{fullName}</span>
              <div className="FullNameAndIbCell__IbAccounts">
                {ibAccounts &&
                  ibAccounts.length &&
                  ibAccounts.map((ibAccount, index) => (
                    <span className="FullNameAndIbCell__IbAccount" key={`${ibAccount}-${index}`}>
                      {ibAccount}
                    </span>
                  ))}
              </div>
            </div>
          </RowLink>
        );
      },
      isPreviewCell: true,
    },
    {
      Header: 'boTags',
      accessor: 'partnerAccounts',
      Cell: ({
        row: {
          original: { partnerAccounts },
        },
      }) => (
        <PartnerTagsCell
          partnerAccounts={partnerAccounts}
          onEdit={setEditingAccount}
          onRemove={(accountId, tag) => setTagToDelete({ accountId, tag })}
        />
      ),
      isPreviewCell: true,
    },
    {
      Header: 'justEmail',
      accessor: 'email',
      Cell: ({
        row: {
          original: { email, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`} isPreviewCell>
          {email}
        </RowLink>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'justPhone',
      accessor: 'phone',
      Cell: ({
        row: {
          original: { phone, id },
        },
      }) => <RowLink link={`/backoffice/users/${id}`}>{phone}</RowLink>,
    },
    {
      Header: 'justMasterIb',
      accessor: 'masterIb',
      Cell: ({
        row: {
          original: { masterIb, partnerAccount, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <div className="PartnersTable__masterIb">
            {masterIb ? (
              <>
                <span className="PartnersTable__masterIb-caption">{masterIb}</span>
                <span className="PartnersTable__masterIb-name">{partnerAccount}</span>
              </>
            ) : (
              '一'
            )}
          </div>
        </RowLink>
      ),
    },
    {
      Header: 'boPartnerClients',
      accessor: 'clients',
      Cell: ({
        row: {
          original: { clients, id },
        },
      }) => <RowLink link={`/backoffice/users/${id}`}>{clients}</RowLink>,
    },
    {
      Header: 'IB Monthly Commission',
      accessor: 'ibMonthlyCommission',
      Cell: ({
        row: {
          original: { ibMonthlyCommission, id },
        },
      }) => {
        let displayValue = '-';
        if (ibMonthlyCommission) {
          if (ibMonthlyCommission.amount && ibMonthlyCommission.currency) {
            displayValue = `${formatAmountWithThousands(ibMonthlyCommission.amount)} ${ibMonthlyCommission.currency}`;
          } else {
            displayValue = String(ibMonthlyCommission);
          }
        }
        return <RowLink link={`/backoffice/users/${id}`}>{displayValue}</RowLink>;
      },
    },
    {
      Header: 'Clients Monthly Net Deposit',
      accessor: 'clientsMonthlyNetDeposit',
      Cell: ({
        row: {
          original: { clientsMonthlyNetDeposit, id },
        },
      }) => {
        let displayValue = '-';
        if (clientsMonthlyNetDeposit) {
          if (clientsMonthlyNetDeposit.amount && clientsMonthlyNetDeposit.currency) {
            displayValue = `${formatAmountWithThousands(clientsMonthlyNetDeposit.amount)} ${clientsMonthlyNetDeposit.currency}`;
          } else {
            displayValue = String(clientsMonthlyNetDeposit);
          }
        }
        return <RowLink link={`/backoffice/users/${id}`}>{displayValue}</RowLink>;
      },
    },
    {
      Header: 'Commission vs. Net Deposit Ratio',
      id: 'commissionNetDepositRatio',
      Cell: ({
        row: {
          original: { ibMonthlyCommission, clientsMonthlyNetDeposit, id },
        },
      }) => {
        let ibAmount = 0;
        let clientsAmount = 0;
        if (ibMonthlyCommission && ibMonthlyCommission.amount) ibAmount = Number(ibMonthlyCommission.amount);
        else if (ibMonthlyCommission) ibAmount = Number(ibMonthlyCommission);
        
        if (clientsMonthlyNetDeposit && clientsMonthlyNetDeposit.amount) clientsAmount = Number(clientsMonthlyNetDeposit.amount);
        else if (clientsMonthlyNetDeposit) clientsAmount = Number(clientsMonthlyNetDeposit);

        let ratio = 0;
        if (clientsAmount !== 0 && !isNaN(clientsAmount) && !isNaN(ibAmount)) {
          ratio = (ibAmount / clientsAmount) * 100;
        }

        return <RowLink link={`/backoffice/users/${id}`}>{ratio.toFixed(2)}%</RowLink>;
      },
    },
    {
      Header: 'Risk Status',
      id: 'riskStatus',
      Cell: ({
        row: {
          original: { ibMonthlyCommission, clientsMonthlyNetDeposit, id },
        },
      }) => {
        let ibAmount = 0;
        let clientsAmount = 0;
        if (ibMonthlyCommission && ibMonthlyCommission.amount) ibAmount = Number(ibMonthlyCommission.amount);
        else if (ibMonthlyCommission) ibAmount = Number(ibMonthlyCommission);
        
        if (clientsMonthlyNetDeposit && clientsMonthlyNetDeposit.amount) clientsAmount = Number(clientsMonthlyNetDeposit.amount);
        else if (clientsMonthlyNetDeposit) clientsAmount = Number(clientsMonthlyNetDeposit);

        let ratio = 0;
        if (clientsAmount !== 0 && !isNaN(clientsAmount) && !isNaN(ibAmount)) {
          ratio = (ibAmount / clientsAmount) * 100;
        }

        const isRisk = ratio >= 40;
        const style = isRisk
          ? {
              color: '#fff',
              backgroundColor: '#ff4d4f',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
            }
          : {
              color: '#52c41a',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
            };

        return (
          <RowLink link={`/backoffice/users/${id}`}>
            <span style={style}>{isRisk ? 'Risk Alert' : 'Normal'}</span>
          </RowLink>
        );
      },
    },
    {
      Header: () => null,
      id: 'clients-list',
      Cell: ({
        row: {
          original: { ibAccounts },
        },
      }) => <ClientsCellWithDropDown ibAccounts={ibAccounts} />,
    },
  ];
  /* eslint-enable */

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const rowProps = (state, rowInfo) => ({
    onClick: () => onRowClick(rowInfo.original.id),
  });

  return (
    <div className="PartnersTable">
      <PaginationTable
        data={data}
        loading={loading}
        columns={columns}
        getList={getList}
        location={location}
        history={history}
        params={params}
        getTrProps={rowProps}
        onRowClick={onRowClick}
        isNewTable
      />
      {editingAccount && (
        <BasicModal caption="Add tags" isOpen onRequestClose={() => setEditingAccount(null)}>
          <UserAddTagForm
            id={editingAccount.id}
            tags={allTags.filter(tag => !(editingAccount.tags || []).some(item => item.id === tag.id))}
            submitTags={submitTags}
            fixedType={PARTNER_FIXED_TAG_TYPE}
            initialValues={{ type: PARTNER_FIXED_TAG_TYPE }}
          />
        </BasicModal>
      )}
      {tagToDelete && (
        <BasicModal
          caption="Are you sure?"
          description="Are you sure want to remove this tag?"
          isOpen
          onRequestClose={() => setTagToDelete(null)}>
          <div className="PartnersTable__confirm-buttons">
            <DefaultButton textId="justYes" butColor="error" onClick={confirmDelete} />
            <DefaultButton textId="justNo" onClick={() => setTagToDelete(null)} filled />
          </div>
        </BasicModal>
      )}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

PartnersTable.propTypes = {
  data: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  getList: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  theme: PropTypes.object,
};

PartnersTable.defaultProps = {
  theme: {},
};

export default withTheme()(PartnersTable);
export { PartnersTable };
